<template>
  <div :id="$attrs['id']" :class="[colorClass]">
    <div v-tooltip="tooltip"
         v-if="visible"
         :style="style"
         :class="[{'hm-floating-button--disabled': disabled === true || processing === true}]"
         class="hm-floating-button"
         @click="throttledOnClick($event)">
      <div class="hm-floating-button__label" v-html="label"></div>
      <icon :icon="disabled === true || processing === true ? 'close' : icon" />
    </div>
    <transition-stagger v-if="hasSlot('default')">
      <div v-show="childrenVisible" class="hm-floating-button__actions">
        <slot />
      </div>
    </transition-stagger>
  </div>
</template>

<script>

import { isNil, isArray, isEmpty, throttle } from 'lodash';
import { TweenMax, TimelineMax } from 'gsap';
import TimerMixin from '../../../mixins/TimerMixin';
import HmFloatingButtonMixin from '../../../mixins/HmFloatingButtonMixin';

export default {
  name: 'HmFloatingButton',

  mixins: [TimerMixin, HmFloatingButtonMixin],

  props: {
    icon: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    color: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    throttle: {
      type: Number,
      default: 1000,
    },
  },

  data: function () {
    return {
      visible: true,
      childrenVisible: false,
      processing: false,
      processingTimer: null,
      throttledOnClick: throttle(this.onClick, this.throttle, { leading: true, trailing: false }),
    };
  },

  computed: {
    style: function () {
      return {
        // eslint-disable-next-line no-underscore-dangle
        opacity: this.$parent.activeChild === null || this._uid === this.$parent.activeChild._uid ? 1 : 0,
        // eslint-disable-next-line no-underscore-dangle
        pointerEvents: this.$parent.activeChild === null || this._uid === this.$parent.activeChild._uid ? 'all' : 'none',
      };
    },
    colorClass: function () {
      return this.color ? `hm-floating-button--${this.color}` : '';
    },
    floatingButtonCount: function () {
      const slot = this.getSlot('default');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    tooltip: function () {
      return isEmpty(this.label) ? null : {
        title: this.label,
        closeOnClickOutside: true,
        delay: 0,
        placement: 'top',
        trigger: 'hover focus',
        html: true,
        preventOverflow: true,
        template: '<div class="tooltip tooltip--floating-button" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
        // boundariesElement: document.querySelector('.roomplan__container'),
        positionFixed: false,
      };
    },
    saveInProgress: function () {
      return this.$store.getters['save/saveInProgress'];
    },
  },

  mounted: function () {
    this.$on('childClicked', () => {
      this.childrenVisible = false;
      if (!this.childrenVisible) this.$parent.$emit('hideChildren');
    });
    this.$parent.$on('hideChildren', () => {
      this.childrenVisible = false;
    });
  },

  methods: {
    addUIBreadcrumb: function (action, metadata = {}) {
      try {
        this.$rollbar.captureEvent('manual', {
          category: 'UI',
          action: action || '-',
          metadata: {
            ...metadata,
            url: window.location.href,
            hotel: this.$root.hotel,
            user: this.$root.username,
            subdomain: this.$store.getters['hotel/hotelSettings'].hotel.tenant.subdomain,
          },
        }, 'info');

        // eslint-disable-next-line no-empty
      } catch (ignore) {
      }
    },
    getSlot: function (slotName) {
      return !isNil(this.$slots[slotName]) ? this.$slots[slotName] : this.$scopedSlots[slotName];
    },
    onClick: function (event) {
      if (this.disabled === true) return;

      const el = this.$el.querySelector('.hm-floating-button');

      const tl = new TimelineMax({});
      tl.add(TweenMax.fromTo(
        el,
        0.25,
        {
          boxShadow: '0px 6px 15px 0px rgba(0,39,46,0.55)',
        },
        {
          boxShadow: 'inset rgba(0, 39, 46, 0.55) 0px 0px 15px -2px',
        },
      ));

      tl.add(TweenMax.fromTo(
        el,
        0.25,
        {
          boxShadow: 'inset rgba(0, 39, 46, 0.55) 0px 0px 15px -2px',
        },
        {
          boxShadow: '0px 6px 15px 0px rgba(0,39,46,0.55)',
        },
      ));

      if (this.floatingButtonCount > 0) {
        if (!this.childrenVisible) {
          // eslint-disable-next-line no-underscore-dangle
          this.$parent.$emit('showChildren', this);
        }
        this.childrenVisible = !this.childrenVisible;
        if (!this.childrenVisible) this.$parent.$emit('hideChildren');
      }
      if (!this.processing) {
        if (this.floatingButtonCount <= 0) {
          this.processing = true;
          this.processingTimer = this.setTimeout(() => {
            this.processing = false;
          }, this.throttle);
        }
        this.$serviceBus.$emit('autosave');
        this.$nextTick(() => {
          this._onClick();
        });
      }
    },
    _onClick: function () {
      if (this.saveInProgress) {
        this.setTimeout(this._onClick, 500);
        this.clearInterval(this.processingTimer);
        this.processingTimer = this.setTimeout(() => {
          this.processing = false;
        }, this.throttle);
      } else {
        this.$emit('click', this);
        this.addUIBreadcrumb(
          'User clicked floating button',
          {
            id: this.id,
            name: this.label,
          },
        );
      }
    },
    hasSlot: function (slotName) {
      return !isNil(this.$slots[slotName]) || !isNil(this.$scopedSlots[slotName]);
    },
    toggle: function () {
      this.visible = !this.visible;
    },
    hide: function () {
      this.visible = false;
    },
    show: function () {
      this.visible = true;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmFloatingButton.vue